/**
 * Ticket 138.1 — typed API client for the multi-line channel admin surface
 * (backend: ADR-019 / VYV-K2.1, `internal/channels`).
 *
 * Deliberately provider-agnostic: every type here is shaped by an adapter's
 * `requiredSecrets` / `requiredConfig` as published by the catalogue endpoint,
 * never by a specific provider name. No Meta (or Twilio, or any other
 * provider) special-casing belongs here — adding a provider to the backend
 * registry must need zero changes in this file.
 *
 * Secrets travel one way only: they are sent on create/rotate and are NEVER
 * part of a response type. `ChannelLine` mirrors the backend `LineView`, which
 * has no field carrying `secrets_enc` content.
 */

import { apiClient } from '@/lib/apiClient';

// ------------------------------------------------------------------ //
// Provider catalogue (GET /channels/providers)
// ------------------------------------------------------------------ //

/** Stable channel identifier (backend `channels.ChannelType`). */
export type ChannelType = string;

/**
 * What ONE line of one channel needs, as declared by the adapter that serves
 * it (backend `channels.ChannelRequirements`). A provider does not necessarily
 * have a single shape: the same Meta app serves WhatsApp (configured by
 * waba_id + phone_number_id, addressed by a phone number) and Messenger
 * (configured by page_id, addressed by a page id and having NO phone number).
 *
 * `phoneNumber` is the DECLARATION that a line of this channel is addressed by
 * a phone number. The form reads it instead of knowing any channel name.
 */
export interface ChannelRequirements {
  channel: ChannelType;
  requiredSecrets: string[];
  requiredConfig: string[];
  phoneNumber: boolean;
  /**
   * Ticket 138.2 — the provider runs its own onboarding popup for THIS channel
   * and this deployment carries the configuration it needs. Per channel because
   * it is per channel in reality: one provider app can serve several channels
   * while its popup onboards only one. The page renders its connect control
   * from this flag and therefore still names no provider and no channel.
   */
  embeddedSignup?: boolean;
}

/**
 * One provider's metadata as published by the adapter registry: the BYOK
 * secret keys and the non-secret config keys a line of that provider needs.
 *
 * `requiredSecrets`/`requiredConfig` are provider-wide; `channelRequirements`
 * is per channel and is AUTHORITATIVE where the two disagree. It is optional
 * so this client keeps working against a backend that predates it.
 */
export interface ChannelProvider {
  provider: string;
  displayName: string;
  channels: ChannelType[];
  requiredSecrets: string[];
  requiredConfig: string[];
  channelRequirements?: ChannelRequirements[];
  /**
   * Ticket 138.2 — the provider runs its own onboarding popup, AND the platform
   * deployment carries the configuration it needs. This is what lets the page
   * offer a "connect" control without naming a provider: it is a catalogue
   * fact, not a hardcoded list. Optional so this client keeps working against a
   * backend that predates it.
   */
  embeddedSignup?: boolean;
}

/**
 * The requirements for `channel` on `provider`: the per-channel declaration
 * when the backend publishes one, else the provider-wide keys with a phone
 * number assumed required (the pre-138.8 shape).
 */
export function requirementsFor(
  provider: ChannelProvider,
  channel: ChannelType,
): ChannelRequirements {
  const declared = provider.channelRequirements?.find((r) => r.channel === channel);
  if (declared) return declared;
  return {
    channel,
    requiredSecrets: provider.requiredSecrets,
    requiredConfig: provider.requiredConfig,
    phoneNumber: true,
    // A backend that predates 138.2 offers no popup. Defaulting to false keeps
    // the page from rendering a control whose endpoint does not exist.
    embeddedSignup: false,
  };
}

export async function getChannelProviders(): Promise<ChannelProvider[]> {
  const { data } = await apiClient.get<ChannelProvider[]>('/channels/providers');
  return data ?? [];
}

// ------------------------------------------------------------------ //
// Lines (GET/POST /channels/lines, POST /channels/lines/{id}/...)
// ------------------------------------------------------------------ //

/**
 * Ticket 138.3 adds `PENDING_REGISTRATION`: the line exists and the number
 * exists at the provider, but the provider's registration ladder has not
 * completed, so the line is MUTE — it cannot send and no webhook reaches
 * Kairos. It is deliberately not `ACTIVE` (a line that stops before
 * registration looks connected and is mute) and deliberately not `ERROR`
 * (nothing failed; it has not finished).
 */
export type LineStatus =
  | 'ACTIVE'
  | 'DISABLED'
  | 'ERROR'
  | 'DISCONNECTED'
  | 'PENDING_REGISTRATION';

/**
 * A tenant's registered line. Mirrors the backend `channels.LineView` — note
 * there is no secrets field: credentials are write-only by construction.
 */
export interface ChannelLine {
  id: string;
  channelType: ChannelType;
  provider: string;
  /** Empty for a channel addressed by `providerLineId` instead of a number. */
  phoneNumber: string;
  providerLineId?: string;
  displayName: string;
  config: Record<string, unknown>;
  status: LineStatus;
  statusReason?: string;
  isDefault: boolean;
  /** Provenance: `native` for a line registered here, else a source-system id. */
  origin: string;
  externalRef?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateChannelLineInput {
  channelType: ChannelType;
  provider: string;
  /** Empty for a channel that has no phone number (Messenger, Instagram DM). */
  phoneNumber: string;
  providerLineId?: string;
  displayName?: string;
  config?: Record<string, string>;
  secrets?: Record<string, string>;
  isDefault?: boolean;
}

export async function listChannelLines(channelType?: ChannelType): Promise<ChannelLine[]> {
  const { data } = await apiClient.get<ChannelLine[]>('/channels/lines', {
    params: channelType ? { channelType } : undefined,
  });
  return data ?? [];
}

export async function createChannelLine(input: CreateChannelLineInput): Promise<ChannelLine> {
  const { data } = await apiClient.post<ChannelLine>('/channels/lines', input);
  return data;
}

export interface RotateChannelLineInput {
  secrets: Record<string, string>;
  config?: Record<string, string>;
}

/** Replaces a line's BYOK credentials. Returns the updated line (200). */
export async function rotateChannelLine(
  id: string,
  input: RotateChannelLineInput,
): Promise<ChannelLine> {
  const { data } = await apiClient.post<ChannelLine>(`/channels/lines/${id}/rotate`, input);
  return data;
}

/** Retains credentials, stops traffic. 204 — callers refetch the list. */
export async function disableChannelLine(id: string): Promise<void> {
  await apiClient.post(`/channels/lines/${id}/disable`);
}

/** Erases credentials, retains config for later re-onboarding. 204. */
export async function disconnectChannelLine(id: string): Promise<void> {
  await apiClient.post(`/channels/lines/${id}/disconnect`);
}

/** Marks the line default for its channel type. 204. */
export async function setDefaultChannelLine(id: string, channelType: ChannelType): Promise<void> {
  await apiClient.post(`/channels/lines/${id}/default`, undefined, {
    params: { channelType },
  });
}

/* -------------------------------------------------------------------------
 * Provider-run onboarding popup (ticket 138.2)
 *
 * Two legs. The first asks the backend for a SIGNED state and the non-secret
 * parameters the popup launches with; the second hands back the popup's
 * short-lived code plus the ids it reported.
 *
 * Note what the admin never types: no WABA id, no phone_number_id, no phone
 * number. They are facts the provider reveals. And note what the second leg
 * does NOT carry: a tenant. The backend takes tenant and line identity
 * exclusively from the signed state, so a caller cannot choose either.
 * ---------------------------------------------------------------------- */

/** Non-secret parameters the browser SDK needs. No app secret is ever here. */
export interface EmbeddedSignupLaunch {
  appId: string;
  configId: string;
  graphVersion: string;
}

export interface EmbeddedSignupStart {
  state: string;
  launch: EmbeddedSignupLaunch;
}

export interface StartEmbeddedSignupInput {
  provider: string;
  channelType: ChannelType;
  displayName?: string;
  isDefault?: boolean;
}

export async function startEmbeddedSignup(
  input: StartEmbeddedSignupInput,
): Promise<EmbeddedSignupStart> {
  const { data } = await apiClient.post<EmbeddedSignupStart>(
    '/channels/lines/onboarding/embedded',
    input,
  );
  return data;
}

export interface CompleteEmbeddedSignupInput {
  state: string;
  code: string;
  wabaId: string;
  phoneNumberId: string;
}

/** Exchanges the popup's code and returns the created line (201). */
export async function completeEmbeddedSignup(
  input: CompleteEmbeddedSignupInput,
): Promise<ChannelLine> {
  const { data } = await apiClient.post<ChannelLine>(
    '/channels/lines/onboarding/embedded/complete',
    input,
  );
  return data;
}

/* -------------------------------------------------------------------------
 * Provider-reported account capabilities (ticket 138.3, AC4)
 *
 * What the PROVIDER last said this tenant's accounts may hold. It is a fact
 * received over a signed webhook, shown so an admin understands a refusal
 * before it happens.
 *
 * It is NOT a rule. Nothing in this client, and nothing on the page that
 * renders it, may refuse to send an onboarding request because of it: a cached
 * cap is a fact about the past, the provider is the only authority on whether
 * one more number is allowed, and blocking on a stale copy would cost the
 * tenant a line it is entitled to.
 * ---------------------------------------------------------------------- */

export interface ChannelAccountCapability {
  provider: string;
  /** The provider's own account id (Meta: the WhatsApp Business Account id). */
  accountRef: string;
  /** The provider-reported maximum registered numbers for the portfolio. */
  maxPhoneNumbers: number;
  /** When the provider said so (ISO-8601). */
  reportedAt: string;
}

export async function listChannelCapabilities(): Promise<ChannelAccountCapability[]> {
  const { data } = await apiClient.get<ChannelAccountCapability[]>('/channels/capabilities');
  return data ?? [];
}

/* -------------------------------------------------------------------------
 * The registration ladder (ticket 138.3)
 *
 * Adoption is a DIFFERENT path from registration and its failures read
 * differently (AC5). `/channels/lines/numbers` is also emphatically not
 * `/channels/lines/adopt`: that one folds in a line a vertical bridge already
 * owns and touches no provider at all.
 * ---------------------------------------------------------------------- */

export type VerificationMethod = 'SMS' | 'VOICE';

export interface AddProviderNumberInput {
  provider: string;
  channelType: ChannelType;
  /** The provider account the number joins (Meta: the WABA id). */
  accountRef: string;
  /** Dialing code without a plus, e.g. "52". */
  countryCode: string;
  /** The national part of the number. */
  phoneNumber: string;
  /** The display name the number will be certified under. */
  verifiedName: string;
  displayName?: string;
  config?: Record<string, string>;
  secrets?: Record<string, string>;
  isDefault?: boolean;
}

/**
 * Adds a number the tenant already owns to a provider account and records it as
 * a line that is explicitly NOT yet usable (`PENDING_REGISTRATION`, 201). The
 * next step is ownership verification, not registration.
 */
export async function addProviderNumber(input: AddProviderNumberInput): Promise<ChannelLine> {
  const { data } = await apiClient.post<ChannelLine>('/channels/lines/numbers', input);
  return data;
}

/**
 * Asks the provider to send an ownership-verification code (202).
 *
 * `VOICE` places an automated call that CANNOT navigate an IVR: it has to reach
 * a person or a mailbox that can read the digits back. See
 * docs/runbooks/meta-number-registration.md.
 */
export async function requestLineVerification(
  id: string,
  method: VerificationMethod,
  language?: string,
): Promise<void> {
  await apiClient.post(`/channels/lines/${id}/verification/request`, { method, language });
}

/** Submits the code the admin received (200). */
export async function confirmLineVerification(id: string, code: string): Promise<void> {
  await apiClient.post(`/channels/lines/${id}/verification/confirm`, { code });
}

/**
 * Runs the last rungs — set the two-step PIN and register the number, then
 * subscribe the platform app to the ACCOUNT — and returns the updated line
 * (200). This is the only call that can turn a line `ACTIVE`.
 */
export async function completeLineRegistration(id: string): Promise<ChannelLine> {
  const { data } = await apiClient.post<ChannelLine>(`/channels/lines/${id}/registration`);
  return data;
}

/* -------------------------------------------------------------------------
 * The codes the backend speaks (ticket 138.3, AC3 and AC6)
 *
 * `writeChannelError` answers a refusal as {"error": <code>, "detail": <text>}
 * and the ladder records `status_reason` as `<code>: <detail>`. The CODE is the
 * contract; the detail is a diagnostic string in English, fit for a support
 * ticket and NOT fit to be shown as a user-facing sentence (ADR-033).
 *
 * These lists exist so the translation coverage is checkable rather than
 * assumed: a test walks them against es, en and pt-BR. Adding a code to the
 * backend without a translation in all three fails that test.
 * ---------------------------------------------------------------------- */

/** Values of the `error` field for a registration-ladder refusal. */
export const CHANNEL_ERROR_CODES = [
  'onboarding_code_rejected',
  'number_cap_reached',
  'display_name_not_approved',
  'number_in_use_on_whatsapp_app',
  'number_already_verified',
  'verification_throttled',
  'verification_code_invalid',
  'registration_rejected',
  'app_subscription_failed',
  'number_registration_unsupported',
  'provider_rejected',
  'registration_pin_missing',
] as const;

/** Leading codes of a line's `statusReason`. */
export const CHANNEL_STATUS_REASON_CODES = [
  'display_name_not_approved',
  'registration_rejected',
  'app_subscription_failed',
  'number_cap_reached',
  'number_in_use_on_whatsapp_app',
  'verification_required',
  'provider_rejected',
  'registration_incomplete',
  'no_registration_credential',
] as const;

export type ChannelErrorCode = (typeof CHANNEL_ERROR_CODES)[number];
export type ChannelStatusReasonCode = (typeof CHANNEL_STATUS_REASON_CODES)[number];

const errorCodeSet = new Set<string>(CHANNEL_ERROR_CODES);
const reasonCodeSet = new Set<string>(CHANNEL_STATUS_REASON_CODES);

/**
 * The known code in an error response, or null. Null for an unknown code too:
 * inventing `channelLines.code.<whatever>` would render the raw key to a user.
 */
export function channelErrorCode(err: unknown): ChannelErrorCode | null {
  const body = (err as { response?: { data?: { error?: unknown } } } | undefined)?.response?.data;
  const code = typeof body?.error === 'string' ? body.error : '';
  return errorCodeSet.has(code) ? (code as ChannelErrorCode) : null;
}

/** The known code prefixing a `statusReason`, or null. */
export function statusReasonCode(reason: string | undefined): ChannelStatusReasonCode | null {
  if (!reason) return null;
  const code = reason.split(':', 1)[0].trim();
  return reasonCodeSet.has(code) ? (code as ChannelStatusReasonCode) : null;
}
